import React, { useCallback, useEffect, useState } from 'react';
import { LoadingAnimation } from './ui/LoadingAnimation';
import type { AppleLoginPayload } from '../lib/authTypes';
import {
  createAppleAuthAttempt,
  formatAppleAuthError,
  getAppleWebConfig,
  waitForAppleAuthCompletion,
} from '../lib/appleAuth';

interface AppleLoginProps {
  onSuccess: (payload: AppleLoginPayload) => void | Promise<void>;
  onError: (error: string) => void;
}

declare global {
  interface Window {
    AppleID?: {
      auth: {
        init: (config: Record<string, unknown>) => void;
        signIn: () => Promise<any>;
      };
    };
  }
}

const APPLE_SCRIPT_URL =
  'https://appleid.cdn-apple.com/appleauth/static/jsapi/appleid/1/en_US/appleid.auth.js';

export const AppleLogin: React.FC<AppleLoginProps> = ({ onSuccess, onError }) => {
  const [scriptLoaded, setScriptLoaded] = useState(false);
  const [isLoading, setIsLoading] = useState(false);

  const initializeAppleSignIn = useCallback((state?: string) => {
    const { config, error } = getAppleWebConfig();

    if (!config || !window.AppleID?.auth) {
      if (error && process.env.NODE_ENV === 'development') {
        console.warn('Apple Sign-In initialization skipped:', error);
      }
      return;
    }

    try {
      window.AppleID.auth.init({
        clientId: config.clientId,
        redirectURI: config.redirectURI,
        scope: 'name email',
        state: state || window.location.pathname || '/',
        usePopup: true,
      });
    } catch (error) {
      if (process.env.NODE_ENV === 'development') {
        console.warn('Apple Sign-In initialization error:', error);
      }
    }
  }, []);

  useEffect(() => {
    const existingScript = document.querySelector(
      `script[src="${APPLE_SCRIPT_URL}"]`
    ) as HTMLScriptElement | null;

    const markLoaded = () => {
      setScriptLoaded(true);
    };

    if (existingScript) {
      if (window.AppleID?.auth) {
        markLoaded();
        return;
      }

      existingScript.addEventListener('load', markLoaded);
      return () => {
        existingScript.removeEventListener('load', markLoaded);
      };
    }

    const script = document.createElement('script');
    script.src = APPLE_SCRIPT_URL;
    script.async = true;
    script.defer = true;
    script.onload = markLoaded;
    script.onerror = () => {
      onError('Failed to load Apple Sign-In script');
    };
    document.head.appendChild(script);

    return () => {
      script.removeEventListener('load', markLoaded);
    };
  }, [onError]);

  useEffect(() => {
    if (scriptLoaded) {
      initializeAppleSignIn();
    }
  }, [initializeAppleSignIn, scriptLoaded]);

  const handleAppleLogin = async () => {
    if (isLoading) return;

    const { config, error } = getAppleWebConfig();
    if (!config) {
      onError(
        error ||
          'Apple Sign-In is not configured. Set REACT_APP_APPLE_CLIENT_ID and REACT_APP_APPLE_REDIRECT_URI.'
      );
      return;
    }

    if (!scriptLoaded || !window.AppleID?.auth) {
      onError('Apple Sign-In not loaded');
      return;
    }

    setIsLoading(true);
    try {
      const attempt = createAppleAuthAttempt(window.location);
      initializeAppleSignIn(attempt.state);

      const payload = await waitForAppleAuthCompletion({
        attempt,
        signInRequest: window.AppleID.auth.signIn(),
      });
      await onSuccess(payload);
    } catch (error: any) {
      onError(formatAppleAuthError(error));
    } finally {
      setIsLoading(false);
    }
  };

  if (!scriptLoaded) {
    return (
      <div className="sso-provider-shell w-full rounded-xl bg-gray-100 p-4 flex items-center justify-center">
        <div className="flex items-center gap-2">
          <LoadingAnimation size={16} />
          <span className="text-gray-600">Loading Apple Sign-In...</span>
        </div>
      </div>
    );
  }

  return (
    <button
      onClick={handleAppleLogin}
      disabled={isLoading}
      className="sso-provider-shell w-full bg-black hover:bg-gray-900 text-white border border-white/10 shadow-sm inline-flex items-center justify-center rounded-xl font-medium transition-colors focus:outline-none focus:ring-2 focus:ring-white/40 focus:ring-offset-2 disabled:opacity-50 disabled:pointer-events-none h-11 px-4 py-2"
      type="button"
    >
      {isLoading ? (
        <div
          className="grid w-full grid-cols-[20px_minmax(0,1fr)_20px] items-center gap-3"
          data-testid="apple-login-button-content"
        >
          <span className="flex h-5 w-5 items-center justify-center" data-testid="apple-login-icon-slot">
            <LoadingAnimation size={16} />
          </span>
          <span className="text-center">Signing in...</span>
          <span aria-hidden="true" />
        </div>
      ) : (
        <div
          className="grid w-full grid-cols-[20px_minmax(0,1fr)_20px] items-center gap-3"
          data-testid="apple-login-button-content"
        >
          <span
            className="flex h-5 w-5 items-center justify-center"
            data-testid="apple-login-icon-slot"
          >
            <svg
              aria-hidden="true"
              className="w-5 h-5"
              viewBox="0 0 24 24"
              fill="currentColor"
              xmlns="http://www.w3.org/2000/svg"
            >
              <path d="M17.05 12.536c-.026-2.745 2.245-4.063 2.348-4.126-1.28-1.87-3.271-2.127-3.979-2.156-1.692-.171-3.304 1.011-4.162 1.011-.873 0-2.19-.994-3.62-.966-1.857.028-3.592 1.102-4.548 2.769-1.971 3.414-.501 8.431 1.388 11.192.945 1.351 2.05 2.858 3.497 2.805 1.416-.058 1.945-.902 3.626-.902 1.665 0 2.162.902 3.638.868 1.519-.024 2.476-1.358 3.388-2.721 1.092-1.548 1.53-3.075 1.548-3.153-.036-.012-2.954-1.134-3.006-4.621Zm-2.735-8.48c.761-.926 1.282-2.19 1.137-3.466-1.101.048-2.478.761-3.272 1.667-.704.799-1.333 2.111-1.171 3.337 1.239.093 2.511-.626 3.306-1.538Z" />
            </svg>
          </span>
          <span className="text-center">Continue with Apple</span>
          <span aria-hidden="true" />
        </div>
      )}
    </button>
  );
};
